import { useState } from 'react'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { FilterBar } from '@/shared/ui/FilterBar'
import { StatusBadge } from '@/shared/ui/Badge'
import { Trash2, Loader2 } from 'lucide-react'
import { formatDate } from '@/shared/utils'
import { useDebounce } from '@/shared/hooks'
import { useHomeListings, useDeleteHomeListing } from '@/api/hooks'
import { Can } from '@/shared/ui/Can'

interface Listing {
  id:number; title?:string; name?:string; school_name?:string; vendor_name?:string
  status?:string; is_active?:boolean; created_at?:string; trainers_count?:number
}

export function HomeListingPage() {
  const [search, setSearch] = useState('')
  const debounced = useDebounce(search)
  const { data, isLoading, isError } = useHomeListings({ search: debounced || undefined })
  const del = useDeleteHomeListing()
  const [toDelete, setToDelete] = useState<Listing | null>(null)

  const rows: Listing[] = data?.data ?? data ?? []

  return (
    <div className="space-y-5">
      <PageHeader title="Home Page Listing"
        subtitle="Schools / trainers featured on the app home page"
        breadcrumbs={[{label:'Dashboard',to:'/dashboard'},{label:'Home Page Listing'}]}/>
      <Card padding={false}>
        <div className="p-5 border-b border-gray-100">
          <FilterBar search={{ value:search, onChange:setSearch, placeholder:'Search listings…' }}/>
        </div>
        {isError && <p className="px-5 py-4 text-sm text-red-500">Failed to load listings.</p>}
        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
        ) : (
          <div className="overflow-x-auto">
          <table className="drivys-table w-full">
            <thead className="bg-gray-50"><tr><th>#</th><th>Title / School</th><th>Trainers</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-gray-400 py-12 text-sm">No home listings found</td></tr>
              ) : rows.map(l => (
                <tr key={l.id}>
                  <td className="font-mono text-xs text-gray-400">#{l.id}</td>
                  <td className="font-medium text-gray-900">{l.title ?? l.name ?? l.school_name ?? l.vendor_name ?? '—'}</td>
                  <td className="text-gray-700">{l.trainers_count ?? '—'}</td>
                  <td><StatusBadge status={l.status ?? (l.is_active===false?'inactive':'active')}/></td>
                  <td className="text-gray-500 text-sm">{l.created_at ? formatDate(l.created_at) : '—'}</td>
                  <td>
                    <Can perm="DELETE /admin/home-listing/*">
                      <button onClick={()=>setToDelete(l)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100"><Trash2 size={13}/></button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <ConfirmDialog open={!!toDelete} onClose={()=>setToDelete(null)}
        onConfirm={async()=>{ if(toDelete) await del.mutateAsync(toDelete.id); setToDelete(null) }}
        title="Remove from home listing?" description={`This will remove "${toDelete?.title ?? toDelete?.name ?? 'this listing'}" from the home page.`} loading={del.isPending}/>
    </div>
  )
}
